import * as React from "react";
import { defaultAgentGroupName, defaultEnvironmentName } from "shared/model/AgentGroup";
import { WorkspaceIcon } from "homePage/model/Workspace";

// --- Event payload definitions ---
interface EnvironmentChangeEvent {
    environmentName: string;
    agentGroupName?: string;
}

interface SearchParametersChangeEvent {
    searchParams: URLSearchParams;
}

interface WorkspaceChangeEvent {
    workspaceSlug: string;
}

interface WorkspaceInfoChangeEvent {
    workspaceSlug: string;
    displayName: string;
    icon: WorkspaceIcon;
}

// Register all custom event types here
interface FrendsNavEventMap {
    frendsEnvironmentChange: EnvironmentChangeEvent;
    frendsSearchParametersChange: SearchParametersChangeEvent;
    frendsWorkspaceChange: WorkspaceChangeEvent;
    frendsWorkspaceInfoChange: WorkspaceInfoChangeEvent;
}

type FrendsNavEventName = keyof FrendsNavEventMap;

// --- Default values per event type ---
const EVENT_DEFAULTS: { [K in FrendsNavEventName]: () => FrendsNavEventMap[K] } = {
    frendsEnvironmentChange: () => ({
        environmentName: defaultEnvironmentName,
        agentGroupName: defaultAgentGroupName,
    }),
    frendsSearchParametersChange: () => ({
        searchParams: new URLSearchParams(),
    }),
    frendsWorkspaceChange: () => ({
        workspaceSlug: "Default",
    }),
    frendsWorkspaceInfoChange: () => ({
        workspaceSlug: "Default",
        displayName: "",
        icon: WorkspaceIcon.Square,
    }),
};

export const useFrendsNavEvent = <T extends FrendsNavEventName>(
    eventName: T,
    callback?: (data: FrendsNavEventMap[T]) => void
) => {
    React.useEffect(() => {
        if (!callback) {
            return;
        }

        const onEvent = (event: Event) => {
            const customEvent = event as CustomEvent<FrendsNavEventMap[T]>;
            if (customEvent.detail) {
                callback(customEvent.detail);
            }
        };

        window.addEventListener(eventName, onEvent);

        return () => {
            window.removeEventListener(eventName, onEvent);
        };
    }, [eventName, callback]);

    const dispatch = (detail?: Partial<FrendsNavEventMap[T]>) => {
        const defaults = EVENT_DEFAULTS[eventName]();
        const event = new CustomEvent(eventName, {
            detail: { ...defaults, ...detail } as FrendsNavEventMap[T],
        });
        window.dispatchEvent(event);
    };

    return { dispatch };
};
